import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircleHeart, X, Send, Sparkles, Loader2 } from "lucide-react";
import { API } from "../lib/api";

const getSid = () => {
  try {
    let s = localStorage.getItem("bnm-companion-sid");
    if (!s) {
      s = "cmp-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
      localStorage.setItem("bnm-companion-sid", s);
    }
    return s;
  } catch (e) {
    return "cmp-" + Date.now();
  }
};

export const ReaderCompanion = ({ chapterTitle }) => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const sessionRef = useRef(getSid());
  const scrollRef = useRef(null);
  const loadedRef = useRef(false);

  useEffect(() => {
    if (!open || loadedRef.current) return;
    loadedRef.current = true;
    fetch(`${API}/companion/history?session_id=${encodeURIComponent(sessionRef.current)}`)
      .then((r) => r.json())
      .then((d) => setMessages(d.messages || []))
      .catch(() => {});
  }, [open]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const streamChat = useCallback(async (text) => {
    setBusy(true);
    setErr("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    try {
      const resp = await fetch(`${API}/companion/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionRef.current, message: text }),
      });
      if (!resp.ok || !resp.body) throw new Error("bad response");
      const reader = resp.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const chunk of parts) {
          const line = chunk.trim();
          if (!line.startsWith("data:")) continue;
          try {
            const d = JSON.parse(line.slice(5).trim());
            if (d.delta) {
              setMessages((m) => {
                const copy = m.slice();
                const last = copy[copy.length - 1];
                copy[copy.length - 1] = { role: "assistant", content: (last?.content || "") + d.delta };
                return copy;
              });
            } else if (d.error) {
              setErr(d.error);
            }
          } catch (e) {
            /* ignore partial */
          }
        }
      }
    } catch (e) {
      setErr("The companion stepped away for a moment. Please try again.");
    } finally {
      setBusy(false);
    }
  }, []);

  const send = () => {
    const t = input.trim();
    if (!t || busy) return;
    setInput("");
    streamChat(t);
  };

  const getPrompt = async () => {
    if (busy) return;
    setBusy(true);
    setErr("");
    try {
      const r = await fetch(`${API}/reflections/prompt`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ chapter_title: chapterTitle || "Chapter One" }),
      });
      const d = await r.json();
      if (d.prompt) setMessages((m) => [...m, { role: "assistant", content: d.prompt }]);
      else setErr("No prompt right now.");
    } catch (e) {
      setErr("Reflection is unavailable right now.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="companion-open"
        aria-label="Open the reading companion"
        className="safe-bottom safe-x fixed bottom-5 left-4 z-[60] flex h-12 w-12 items-center justify-center rounded-full bg-ink text-ivory shadow-[0_14px_34px_-16px_rgba(30,34,41,0.8)] ring-1 ring-white/20 transition hover:scale-105"
      >
        <MessageCircleHeart className="h-5 w-5" strokeWidth={1.6} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 24 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            data-testid="companion-drawer"
            className="safe-bottom safe-x fixed bottom-4 left-4 right-4 z-[65] flex max-h-[78dvh] flex-col overflow-hidden rounded-2xl bg-ivory-50 shadow-[0_30px_80px_-30px_rgba(30,34,41,0.6)] ring-1 ring-ivory-400/60 sm:right-auto sm:w-[380px]"
          >
            <div className="flex items-center justify-between border-b border-ivory-400/50 px-4 py-3">
              <div>
                <p className="font-display text-base text-ink">A quiet companion</p>
                <p className="text-[11px] text-ink-muted">Reflections as you read — not therapy.</p>
              </div>
              <button onClick={() => setOpen(false)} data-testid="companion-close" aria-label="Close" className="text-ink-muted transition hover:text-ink">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-4" data-testid="companion-messages">
              {messages.length === 0 && (
                <p className="text-sm leading-relaxed text-ink-soft">
                  However you arrived here, you're welcome. Share whatever is present, or ask for a reflection to sit with.
                </p>
              )}
              {messages.map((m, i) => (
                <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
                  <div
                    data-testid={`companion-msg-${m.role}`}
                    className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                      m.role === "user" ? "bg-ink text-ivory" : "bg-white text-ink ring-1 ring-ivory-400/60"
                    }`}
                  >
                    {m.content || (busy ? "…" : "")}
                  </div>
                </div>
              ))}
            </div>

            {err && <p className="px-4 pb-1 text-xs text-terracotta" data-testid="companion-error">{err}</p>}

            <div className="border-t border-ivory-400/50 p-3">
              <button
                onClick={getPrompt}
                disabled={busy}
                data-testid="companion-prompt"
                className="mb-2 inline-flex items-center gap-1.5 rounded-full border border-gold/40 px-3 py-1.5 text-xs text-gold-deep transition hover:border-gold hover:bg-gold/10 disabled:opacity-50"
              >
                <Sparkles className="h-3.5 w-3.5" /> Give me a reflection prompt
              </button>
              <div className="flex items-end gap-2">
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                  placeholder="Share what's present…"
                  rows={1}
                  data-testid="companion-input"
                  className="max-h-28 flex-1 resize-none rounded-xl border border-ink/15 bg-white px-3 py-2 text-sm text-ink placeholder:text-ink-muted/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold/50"
                />
                <button
                  onClick={send}
                  disabled={busy || !input.trim()}
                  data-testid="companion-send"
                  aria-label="Send"
                  className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-ink text-ivory transition hover:bg-ink/90 disabled:opacity-40"
                >
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
